import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { connect } from 'react-redux';
import { loginAsGuest } from '../../ducks/auth.duck';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { fetchCommission } from '../../util/api';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify, createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { userDisplayNameAsString } from '../../util/data';
import { LINE_ITEM_CUSTOMER_COMMISSION } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { showNewMessage } from '@intercom/messenger-js-sdk';

import { ModalInMobile, PrimaryButton, AvatarSmall, H1, H2, Modal, SecondaryButton, IconIntercom } from '../../components';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);
import InlineOrderForm from './InlineOrderForm';

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

// Custom hook to fetch commission data from Sharetribe Console
const useCommissionData = () => {
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetchCommission()
      .then(response => {
        if (mounted) {
          setCommissionData(response.data);
          setError(null);
        }
      })
      .catch(err => {
        if (mounted) {
          setError(err);
          // Fallback to null so we can use default config
          setCommissionData(null);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { commissionData, loading, error };
};

// Simple customer commission calculation for price display
// This only applies the basic customer commission percentage without Stripe calculations
const calculateSimpleCustomerCommission = (basePrice, customerCommissionPercentage) => {
  const commissionAmount = Math.round((basePrice.amount * customerCommissionPercentage) / 100);
  return new Money(basePrice.amount + commissionAmount, basePrice.currency);
};

// Utility function to calculate commission-adjusted price for customers
const calculateCustomerPriceWithCommission = (basePrice, config, currentUser, commissionData) => {
  if (!basePrice) {
    return basePrice;
  }

  // Only show commission-adjusted price for customers (not providers/owners)
  const userType = currentUser?.attributes?.profile?.publicData?.userType;

  // Use commission data from Sharetribe Console if available, otherwise fallback to config
  let customerCommissionPercentage;
  
  if (commissionData?.customerCommission?.percentage) {
    customerCommissionPercentage = commissionData.customerCommission.percentage;
  } else if (config?.commission?.showCustomerCommission) {
    customerCommissionPercentage = config.commission.defaultCustomerCommissionPercentage;
  } else {
    // No commission configuration found, show base price
    return basePrice;
  }

  // Apply simple customer commission (just percentage of base price)
  return calculateSimpleCustomerCommission(basePrice, customerCommissionPercentage);
};

const priceData = (price, currency, intl, config = null, currentUser = null, commissionData = null) => {
  if (price && price.currency === currency) {
    // Calculate commission-adjusted price for customers
    const adjustedPrice = calculateCustomerPriceWithCommission(price, config, currentUser, commissionData);
    const formattedPrice = formatMoney(intl, adjustedPrice);
    
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search, {}, true), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search, {}, true), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location,
) => {

  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    config = null,
    currentUser = null,
    commissionData = null,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  // Include config, currentUser, and commissionData to calculate commission-adjusted price for customers
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl, config, currentUser, commissionData);
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>{formattedPrice}</p>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    metadata,
    listingConfig,
    description,
    isIframe,
    pageUrl,
    showOfferModal,
    setShowOfferModal,
    fromListingPage,
    offerPrice,
    counterOfferPrice,
    currentUser,
    selectedDelivery,
    showSupportChat,
  } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const { commissionData, loading: commissionLoading, error: commissionError } = useCommissionData();

  let { userType = 'buyer' } = currentUser?.attributes?.profile?.publicData || {};
  let isGuestOrBuyer = userType == 'buyer';
  const [copied, setCopied] = useState(false);

  // Product questions go to BowTackle support, not to the seller: the client
  // asked for a live chat capability because these questions were reaching
  // Perry by email and phone only. See docs/intercom-messenger-current-behavior.md
  const onOpenSupportChat = () => {
    // Not the `title` prop: callers pass either null or a React element there,
    // neither of which survives being interpolated into a plain-text message.
    const listingTitle = listing?.attributes?.title || '';
    // Built from the route configuration rather than the current address bar:
    // `pageUrl` is the iframe/share URL (always `?iframe`-suffixed) and
    // `window.location` would leak whatever host the visitor happens to be on.
    // Support needs the canonical marketplace link.
    const listingPath = createResourceLocatorString('ListingPage', routeConfiguration, {
      id: listing?.id?.uuid,
      slug: createSlug(listingTitle),
    });
    const listingUrl = `${config.marketplaceRootURL}${listingPath}`;
    showNewMessage(
      intl.formatMessage({ id: 'OrderPanel.supportChatPrefill' }, { listingTitle, listingUrl })
    );
  };

  const { listingFields } = listingConfig || {};
  const publicData = listing?.attributes?.publicData || {};
  
  // Check if this is a manufacturer listing (should not be purchasable)
  const isManufacturerListing = publicData?.isManufacturer === true;
  const isFromManufacturer = publicData?.isFromManufacturer === true;

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, includeForListingTypes, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType =
      includeForListingTypes == null || includeForListingTypes.includes(listingType);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue || metadataValue;

    if (isDetail && isTargetListingType && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label })
        : schemaType === 'boolean'
          ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
          : schemaType === 'long'
            ? filteredConfigs.concat({ key, value, label })
            : filteredConfigs;
    }
    return filteredConfigs;
  };

  const existingListingFields = listingFields && listingFields.length ? listingFields.reduce(pickListingFields, []) : [];

  const { listingType, unitType, transactionProcessAlias = '' } = publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;
  const showInlineBookingForm = !isIframe && (showBookingTimeForm || showBookingDatesForm);

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled, source: listingSource, shippingMethod: listingShippingMethod } = listing?.attributes?.publicData || {};
  const isEproFlatRate = (listingSource === 'epro' || listingSource === 'wri') && listingShippingMethod !== 'buyer';

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  // Manufacturer listings are now allowed to proceed to checkout with store selection

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const modalBody = showPriceMissing ? (
    <PriceMissing />
  ) : showInvalidCurrency ? (
    <InvalidCurrency />
  ) : showBookingTimeForm ? (
    <BookingTimeForm
      className={css.bookingForm}
      formId="OrderPanelBookingTimeForm"
      lineItemUnitType={lineItemUnitType}
      onSubmit={onSubmit}
      price={price}
      marketplaceCurrency={marketplaceCurrency}
      dayCountAvailableForBooking={dayCountAvailableForBooking}
      listingId={listing.id}
      isOwnListing={isOwnListing}
      monthlyTimeSlots={monthlyTimeSlots}
      onFetchTimeSlots={onFetchTimeSlots}
      startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
      endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
      timeZone={timeZone}
      marketplaceName={marketplaceName}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      payoutDetailsWarning={payoutDetailsWarning}
    />
  ) : showBookingDatesForm ? (
    <BookingDatesForm
      className={css.bookingForm}
      formId="OrderPanelBookingDatesForm"
      lineItemUnitType={lineItemUnitType}
      onSubmit={onSubmit}
      price={price}
      marketplaceCurrency={marketplaceCurrency}
      dayCountAvailableForBooking={dayCountAvailableForBooking}
      listingId={listing.id}
      isOwnListing={isOwnListing}
      monthlyTimeSlots={monthlyTimeSlots}
      onFetchTimeSlots={onFetchTimeSlots}
      startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
      endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
      timeZone={timeZone}
      marketplaceName={marketplaceName}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      payoutDetailsWarning={payoutDetailsWarning}
    />
  ) : showProductOrderForm ? (
    <ProductOrderForm
      history={history}
      isAuthenticated={!!currentUser}
      formId="OrderPanelProductOrderForm"
      onSubmit={onSubmit}
      price={price}
      marketplaceCurrency={marketplaceCurrency}
      currentStock={currentStock}
      allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
      pickupEnabled={pickupEnabled && displayPickup}
      shippingEnabled={shippingEnabled && displayShipping}
      displayDeliveryMethod={displayPickup || displayShipping}
      listingId={listing.id}
      isOwnListing={isOwnListing}
      marketplaceName={marketplaceName}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      onContactUser={onContactUser}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      payoutDetailsWarning={payoutDetailsWarning}
      showOfferModal={showOfferModal}
      setShowOfferModal={setShowOfferModal}
      offerPrice={offerPrice}
      counterOfferPrice={counterOfferPrice}
      isIframe={isIframe}
      loginAsGuest={props.loginAsGuest}
      selectedDelivery={selectedDelivery}
    />
  ) : showInquiryForm && !isManufacturerListing ? (
    <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
  ) : !isKnownProcess ? (
    <p className={css.errorSidebar}>
      <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
    </p>
  ) : null;

  const shareLink = () => {
    navigator.clipboard.writeText(pageUrl);

    let seconds = 5;

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, seconds * 1000);
  };

  return (
    <div className={classes}>

      {/* Share listing button removed for iframe */}

      {/* Inline Order Form (with Add to Cart) — shown in both normal and iframe mode */}
      {showProductOrderForm && !isClosed ? (
        <InlineOrderForm
          formId="InlineProductOrderForm"
          onSubmit={onSubmit}
          currentStock={currentStock}
          pickupEnabled={pickupEnabled && displayPickup}
          shippingEnabled={shippingEnabled && displayShipping}
          displayDeliveryMethod={displayPickup || displayShipping}
          selectedDelivery={selectedDelivery}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          isOutOfStock={isOutOfStock}
          setShowOfferModal={setShowOfferModal}
          currentUser={currentUser}
          history={history}
          isIframe={isIframe}
          listing={listing}
          authorName={authorDisplayName}
          loginAsGuest={(values) => {
            const guestEmail = process.env.REACT_APP_GUEST_EMAIL;
            const guestPassword = process.env.REACT_APP_GUEST_PASSWORD;

            if (!guestEmail || !guestPassword) {
              history.push(isIframe ? '/signup?iframe' : '/signup');
              return;
            }

            props.loginAsGuest(guestEmail, guestPassword)
              .then((result) => {
                if (result && result.error) {
                  history.push(isIframe ? '/signup?iframe' : '/signup');
                  return;
                }

                setTimeout(() => {
                  onSubmit(values);
                }, 500);
              })
              .catch(error => {
                history.push(isIframe ? '/signup?iframe' : '/signup');
              });
          }}
        />
      ) : isClosed ? (
        <div className={css.closedListingButton}>
          <FormattedMessage id="OrderPanel.closedListingButtonText" />
        </div>
      ) : null}

      {/* In normal PDP mode, bookings need the form mounted inline because the legacy CTA shell is hidden. */}
      {showInlineBookingForm ? modalBody : null}

      {/* Modal forms for non-product types in iframe mode (booking, inquiry) */}
      {isIframe && !showProductOrderForm ? modalBody : null}

      {!isIframe && !showInlineBookingForm ? <div className={classNames(css.openOrderForm, {[css.openGuestOrderForm]: isGuestOrBuyer})}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
          config={config}
          currentUser={currentUser}
          commissionData={commissionData}
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <>
            <PrimaryButton
              className={css.purchaseButton}
              onClick={handleSubmit(
                isOwnListing,
                isClosed,
                showInquiryForm,
                onSubmit,
                history,
                location
              )}
              disabled={isOutOfStock || isManufacturerListing}
            >
              {isBooking ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
              ) : isOutOfStock ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
              ) : isPurchase ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              ) : (
                <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
              )}
            </PrimaryButton>
            <SecondaryButton className={css.offerButton} type="button" inProgress={false} disabled={isOutOfStock || isManufacturerListing} onClick={() => {
              if (!!currentUser) {
                setShowOfferModal(!showOfferModal);
              } else {
                if (isIframe) {
                  history.push('/signup?iframe');
                } else {
                  history.push('/signup');
                }
              }
            }}>
              <FormattedMessage id="OrderPanel.offerButton" />
            </SecondaryButton>
            {isGuestOrBuyer && showSupportChat ? <PrimaryButton
              className={css.intercomButton}
              onClick={onOpenSupportChat}
              title={intl.formatMessage({ id: 'OrderPanel.supportChatButtonLabel' })}
            ><IconIntercom/></PrimaryButton> : null}
          </>
        )}
      </div> : null}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  // Opt-in: only the listing page offers BowTackle support chat.
  showSupportChat: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  pageUrl: '',
};

OrderPanel.propTypes = {
  rootClassName: string,
  pageUrl: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  showSupportChat: bool,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapDispatchToProps = dispatch => ({
  loginAsGuest: (email, password) => {
    // Return the promise from the thunk
    return dispatch(loginAsGuest(email, password));
  },
});

export default compose(
  withRouter,
  injectIntl,
  connect(null, mapDispatchToProps)
)(OrderPanel);
